import { PEOPLE, OVERVIEW, leaderName } from "./data";
import { nextText } from "./ui";

export const COMFORTABLE_LOAD = 12;

export const DAYS = { harold: 2, grace: 6, tomas: 5, hannah: 1, ben: 8, eleanor: 41, frank: 12, kwame: 16, lucy: 21, patricia: 4, sam: 25, marjorie: 3, walter: 4, naomi: 3 };

const named = Object.keys(DAYS).map((id) => {
  const p = PEOPLE[id];
  return {
    id, first: p.first, last: p.last, photo: p.photo, caregiver: p.caregiver, days: DAYS[id], reason: p.reason, lastSeen: p.lastSeen,
    active: p.status === "active", paused: p.status === "paused", pastDue: p.next.tone === "past" ? nextText(p) : null,
  };
});

const S = (name, caregiver, days, reason, method, extra = {}) => ({
  id: name.toLowerCase().replace(/[^a-z]+/g, "-"), name, caregiver, days, reason, lastSeen: `${method}, ${days} days ago`, ...extra,
});
const special = [
  S("Abe Lindgren", "ruth", 71, "Stopped coming after his stroke", "Phone call"),
  S("Victor Ames", "nate", 66, "Works rotating night shifts", "Text"),
  S("Doris Kemp", "marcus", 63, "Homebound since last winter", "Visit"),
  S("Gerald Fenn", "ruth", 52, "Hasn't been back since his wife's funeral", "Phone call"),
  S("Harriet Cole", null, 48, "Just moved into Brookside Senior Living", "Visit"),
  S("Mei Lin Zhou", "james", 38, "Caring for her father at home", "Text"),
  S("Thomas & Bea Arnold", "ruth", 35, "Wintering in Arizona until April", "Email"),
  S("Celia Ward", "marcus", 27, "Started a new job in the city", "Text"),
  S("Priya Nair", "lena", 22, "Twins started kindergarten", "In person"),
  S("Owen Pratt", "james", 18, "Back from a year overseas", "Phone call"),
  S("Carol Jenkins", "ruth", 6, "Her father, Walter, is in hospice", "Phone call", { active: true, pastDue: "Follow-up was due 6 days ago" }),
  S("Peter Lang", "ruth", 13, "Recovering from a heart procedure", "Visit", { active: true, pastDue: "Follow-up was due 9 days ago" }),
  S("Ana Sofia Diaz", "james", 9, "Starting over after her divorce", "In person", { active: true, pastDue: "Follow-up was due yesterday" }),
];

const FIRST = ["Ada", "Brian", "Chloe", "Derek", "Esther", "Felix", "Gloria", "Henry", "Irene", "Jonah", "Keisha", "Leo", "Maya", "Nolan", "Olive", "Paul", "Quinn", "Rosa", "Silas", "Tara", "Uriah", "Vera", "Wes", "Ximena", "Yusuf", "Zoe", "Amos", "Bianca", "Caleb", "Dina", "Ezra"];
const LAST = ["Abbott", "Baker", "Castillo", "Dunn", "Ellis", "Foster", "Garcia", "Hayes", "Ingram", "Jensen", "Kowalski", "Lopez", "Mason", "Nguyen", "Okoro", "Patel", "Quintero", "Reyes", "Shaw", "Turner", "Underwood", "Vasquez", "Walsh", "Xu", "Young", "Zimmer", "Archer", "Bishop", "Coleman"];
const CONTEXT = ["Sings in the choir", "Serves on the welcome team", "New member this fall", "Kids are in the youth group", "Retired teacher", "Volunteers at the food pantry", "Homeschools three kids", "Grad student at State", "Ushers most Sundays", "Leads a small group", "Works as an ER nurse", "Just moved here from Ohio"];
const ACTIVE = ["Recovering from knee surgery", "Husband moved into memory care", "Recently widowed", "Caring for a parent with dementia", "Son deployed overseas", "Separated this summer", "Waiting on biopsy results"];
const METHODS = ["Phone call", "Text", "Visit", "In person", "Email"];
const TARGET = { monica: 12, david: 9, lena: 7, james: 9, ruth: 14, marcus: 10, elaine: 11, nate: 13, beth: 12, kofi: 11 };
const ago = (d) => (d === 0 ? "today" : d === 1 ? "yesterday" : `${d} days ago`);

let seed = 11;
const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
const base = [...named, ...special];
const slots = [];
Object.entries(TARGET).forEach(([id, n]) => {
  for (let i = base.filter((p) => p.caregiver === id).length; i < n; i++) slots.push(id);
});
while (slots.length < 93) slots.push(null);
for (let i = slots.length - 1; i > 0; i--) {
  const j = Math.floor(rnd() * (i + 1));
  [slots[i], slots[j]] = [slots[j], slots[i]];
}
const generated = slots.map((caregiver, i) => {
  const days = i < 8 ? 15 + i * 2 : i < 43 ? (i - 8) % 8 : 8 + ((i - 43) % 7);
  const active = i % 15 === 4;
  return {
    id: `g${i}`, name: `${FIRST[i % 31]} ${LAST[(i * 7) % 29]}`, caregiver, days, active,
    reason: active ? ACTIVE[Math.floor(i / 15)] : CONTEXT[i % CONTEXT.length], lastSeen: `${METHODS[i % 5]}, ${ago(days)}`,
  };
});

export const DIRECTORY = [...base, ...generated];

const withAssigned = (assigned) => DIRECTORY.map((p) => (assigned[p.id] ? { ...p, caregiver: assigned[p.id] } : p));
const isOutside = (p, r) => !p.paused && p.days > r;
const decorate = (p, r) => ({
  ...p,
  next: p.pastDue ? { text: p.pastDue, tone: "past" } : isOutside(p, r) ? { text: "Ready for a check-in", tone: "rhythm" } : null,
});
const rank = (p) => (p.next?.tone === "past" ? 0 : p.next ? 1 : 2);
const sortNeeds = (a, b) => rank(a) - rank(b) || b.days - a.days;

const LISTS = {
  attention: { title: (n) => `${n} people need attention`, hint: "A follow-up is past its date, or they're outside your care rhythm.", test: (p, r) => p.pastDue || isOutside(p, r) },
  month: { title: (n) => `${n} people were reached this month`, hint: "A meaningful care touch logged in the last 30 days.", test: (p) => p.days <= 30 },
  covered: { title: (n) => `${n} people have a caregiver`, hint: "Each has one person who owns their next step.", test: (p) => !!p.caregiver },
  coverage: { title: (n) => `${n} people don't have a caregiver yet`, hint: "Everyone in your care should have one person who owns their next step.", test: (p) => !p.caregiver },
  outside: { title: (n, r) => `${n} people are outside your ${r}-day care rhythm`, hint: "No meaningful connection logged in that time. Not a judgment — just a nudge.", test: isOutside },
  active: { title: (n) => `${n} people in active care`, hint: "Someone is walking with them through something right now.", test: (p) => p.active },
  pastdue: { title: (n) => `${n} follow-ups are past their date`, hint: "A leader planned to reach out but hasn't logged it yet.", test: (p) => !!p.pastDue },
  week: { title: (n) => `${n} people were connected this week`, hint: "A meaningful touch logged in the last 7 days.", test: (p) => p.days <= 7 },
};

export const listFor = (key, r, assigned) => {
  const all = withAssigned(assigned).map((p) => decorate(p, r));
  if (key.startsWith("leader:")) {
    const id = key.split(":")[1];
    const items = all.filter((p) => p.caregiver === id).sort(sortNeeds);
    return { title: `${leaderName(id)}'s people`, hint: `${items.length} people · most leaders carry about ${COMFORTABLE_LOAD} comfortably`, items };
  }
  const l = LISTS[key];
  const items = all.filter((p) => l.test(p, r)).sort(sortNeeds);
  return { title: l.title(items.length, r), hint: l.hint, items };
};

export const leaderStats = (id, r, assigned) => {
  const mine = withAssigned(assigned).filter((p) => p.caregiver === id);
  const newNeeds = OVERVIEW.unassigned.filter((u) => assigned[u.id] === id).length;
  return {
    people: mine.length + newNeeds,
    outside: mine.filter((p) => isOutside(p, r)).length,
    pastDue: mine.filter((p) => p.pastDue).length,
    needs: mine.filter((p) => p.pastDue || isOutside(p, r)).length,
  };
};

export const leaderSummary = (s) => {
  if (!s.needs) return { text: "All caught up", tone: "calm" };
  if (s.pastDue === s.needs) return { text: `${s.pastDue} follow-up${s.pastDue > 1 ? "s" : ""} past due`, tone: "sun" };
  return { text: `${s.needs} need${s.needs > 1 ? "" : "s"} attention`, tone: "sun" };
};

export const metrics = (r, assigned) => {
  const all = withAssigned(assigned);
  const monitored = all.filter((p) => !p.paused);
  const covered = all.filter((p) => p.caregiver).length;
  const outside = monitored.filter((p) => p.days > r).length;
  return {
    total: all.length, covered, coveragePct: Math.round((covered / all.length) * 100), noCaregiver: all.length - covered,
    monitored: monitored.length, inRhythm: monitored.length - outside, rhythmPct: Math.round(((monitored.length - outside) / monitored.length) * 100), outside,
    active: all.filter((p) => p.active).length, pastDue: all.filter((p) => p.pastDue).length, week: all.filter((p) => p.days <= 7).length,
    attention: all.filter((p) => LISTS.attention.test(p, r)).length, month: all.filter((p) => p.days <= 30).length,
  };
};
